import React, { useMemo, useState, useCallback } from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  ColumnDef,
  SortingState,
  ColumnFiltersState,
  ColumnOrderState,
} from '@tanstack/react-table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Table from '@mui/material/Table';

import Typography from '@components/Atoms/Typography';
import Tooltip from '@components/Atoms/Tooltip';
import Box from '../Atoms/Box';
import Button from '../Atoms/Button';
import useGroupColumnManager from './useGroupColumnManager';
import { maxHeight } from '@mui/system';

// Core data interfaces
export interface Score {
  id: string;
  averageScore: number;
  subScores?: Score[];
}

export interface ScoreData {
  id: string;
  name: string;
  score: Score;
}

export interface HierarchicalNode {
  nodeName: string;
  id: string;
  subNodes: HierarchicalNode[];
}

export interface AutoHierarchicalCol {
  ctxKey: string;
  closedValueGetter: (params: {
    data: ScoreData;
    colId: string;
  }) => string | number | null;
  cellComponent?: React.ComponentType<{
    value: string | number | null;
    isHorizontal: boolean;
    pathKey: string;
    data: ScoreData;
    colId: string;
    report?: unknown;
    columnIndex?: number;
  }>;
  report?: unknown;
}

interface TSAdvancedHierarchicalScoreTableProps {
  data: ScoreData[];
  height?: string;
  width?: string;
  columnContext?: Record<string, HierarchicalNode>;
  columns: Array<ColumnDef<ScoreData> | AutoHierarchicalCol>;
  defaultOpenFirstColumn?: boolean;
  hiddenColumns?: string[];
}

// ColumnState interface is now imported from the reducer
export const HeaderTypography: React.FC<{
  nodeName: string;
  isHorizontal: boolean;
}> = ({ nodeName, isHorizontal }) =>
  isHorizontal ? (
    <Typography
      variant="body2"
      sx={{
        fontWeight: 'bold',
      }}
    >
      {nodeName}
    </Typography>
  ) : (
    <Typography
      variant="body2"
      sx={{
        fontWeight: 'bold',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxHeight: '20ch',
      }}
    >
      {nodeName}
    </Typography>
  );

// Header component for group columns
const GroupHeader: React.FC<{
  nodeName: string;
  isOpen: boolean;
  shouldBeHorizontal: boolean;
  onClick: () => void;
}> = ({ nodeName, isOpen, shouldBeHorizontal, onClick }) => (
  <Tooltip title={nodeName} leaveDelay={200} arrow>
    <span>
      <Button
        className="group-header"
        sx={{
          display: 'flex',
          alignItems: shouldBeHorizontal ? 'center' : 'flex-start',
          justifyContent: 'flex-start',
          gap: 0.5,
          cursor: 'pointer',
          userSelect: 'none',
          padding: '4px',
          backgroundColor: isOpen ? '#e3f2fd' : '#f5f5f5',
          borderRadius: '4px',
          border: '1px solid #ddd',
          minHeight: '30px',
          minWidth: 'fit-content',
          writingMode: shouldBeHorizontal ? 'horizontal-tb' : 'vertical-rl',
          textOrientation: 'mixed',
          transform: shouldBeHorizontal ? 'none' : 'rotate(0deg)',
          transition: 'all 0.3s ease',
        }}
        onClick={onClick}
        startIcon={isOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}
      >
        <HeaderTypography nodeName={nodeName} isHorizontal={shouldBeHorizontal} />
      </Button>
    </span>
  </Tooltip>
);

// Header component for leaf nodes with orientation toggle
const LeafNodeHeader: React.FC<{
  nodeName: string;
  isHorizontal: boolean;
  onToggleOrientation: () => void;
}> = ({ nodeName, isHorizontal, onToggleOrientation }) => (
  <Tooltip title={nodeName} leaveDelay={200} arrow>
    <span>
      <Button
        className="leaf-header"
        sx={{
          display: 'flex',
          alignItems: isHorizontal ? 'center' : 'flex-start',
          justifyContent: 'flex-start',
          gap: 0.5,
          cursor: 'pointer',
          userSelect: 'none',
          padding: '4px',
          backgroundColor: isHorizontal ? '#e8f5e8' : '#fff3cd',
          borderRadius: '4px',
          border: '1px solid #ddd',
          minHeight: '30px',
          minWidth: 'fit-content',
          writingMode: isHorizontal ? 'horizontal-tb' : 'vertical-rl',
          textOrientation: 'mixed',
          transform: isHorizontal ? 'none' : 'rotate(0deg)',
          transition: 'all 0.3s ease',
        }}
        onClick={onToggleOrientation}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            transform: isHorizontal ? 'none' : 'rotate(90deg)',
            transition: 'transform 0.3s ease',
          }}
        >
          {isHorizontal ? <ExpandMoreIcon /> : <ChevronRightIcon />}
        </Box>
        <HeaderTypography nodeName={nodeName} isHorizontal={isHorizontal} />
      </Button>
    </span>
  </Tooltip>
);

// Cell component for group columns
const GroupCell: React.FC<{
  value: string | number | null;
  isHorizontal: boolean;
  pathKey: string;
}> = ({ value, isHorizontal, pathKey }) => (
  <Box
    sx={{
      textAlign: 'center',
      padding: '2px',
      backgroundColor: isHorizontal ? '#f8f9fa' : '#e9ecef',
      border: '1px solid #dee2e6',
      borderRadius: '4px',
    }}
    data-orientation={isHorizontal ? 'horizontal' : 'vertical'}
    data-column-path={pathKey}
  >
    {value ?? '--'}
  </Box>
);

// Cell component for leaf nodes with orientation
const LeafNodeCell: React.FC<{
  value: string | number | null;
  isHorizontal: boolean;
  pathKey: string;
}> = ({ value, isHorizontal, pathKey }) => (
  <Box
    sx={{
      textAlign: 'center',
      padding: '2px',
      backgroundColor: isHorizontal ? '#e8f5e8' : '#fff3cd',
      border: '1px solid #dee2e6',
      borderRadius: '4px',
      writingMode: isHorizontal ? 'horizontal-tb' : 'vertical-rl',
      textOrientation: 'mixed',
      transform: isHorizontal ? 'none' : 'rotate(0deg)',
      transition: 'all 0.3s ease',
    }}
    data-orientation={isHorizontal ? 'horizontal' : 'vertical'}
    data-column-path={pathKey}
  >
    {value ?? '--'}
  </Box>
);

// Helper functions to create header and cell renderers
const createGroupHeaderRenderer = (
  nodeName: string,
  isOpen: boolean,
  shouldBeHorizontal: boolean,
  onClick: () => void,
) => {
  const HeaderComponent = () => (
    <GroupHeader
      nodeName={nodeName}
      isOpen={isOpen}
      shouldBeHorizontal={shouldBeHorizontal}
      onClick={onClick}
    />
  );
  HeaderComponent.displayName = 'GroupHeaderRenderer';
  return HeaderComponent;
};

const createGroupCellRenderer = (
  value: string | number | null,
  isHorizontal: boolean,
  pathKey: string,
) => <GroupCell value={value} isHorizontal={isHorizontal} pathKey={pathKey} />;

const createLeafNodeHeaderRenderer = (
  nodeName: string,
  isHorizontal: boolean,
  onToggleOrientation: () => void,
) => {
  const HeaderComponent = () => (
    <LeafNodeHeader
      nodeName={nodeName}
      isHorizontal={isHorizontal}
      onToggleOrientation={onToggleOrientation}
    />
  );
  HeaderComponent.displayName = 'LeafNodeHeaderRenderer';
  return HeaderComponent;
};

const createLeafNodeCellRenderer = (
  value: string | number | null,
  isHorizontal: boolean,
  pathKey: string,
) => <LeafNodeCell value={value} isHorizontal={isHorizontal} pathKey={pathKey} />;

const TSAdvancedHierarchicalScoreTable: React.FC<TSAdvancedHierarchicalScoreTableProps> =
  ({ data, height, width, columnContext, columns, defaultOpenFirstColumn, hiddenColumns }) => {
    const [sorting, setSorting] = useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [columnOrder, setColumnOrder] = useState<ColumnOrderState>([]);

    // Leaf node orientation state - managed by each leaf node itself
    const [leafNodeOrientations, setLeafNodeOrientations] = useState<
      Record<string, boolean>
    >({});

    // Convert columnContext to array format for the hook
    const columnContextArray = useMemo(() => {
      if (!columnContext) {
        return [];
      }
      return Object.values(columnContext);
    }, [columnContext]);

    // Extract first column ID if defaultOpenFirstColumn is true
    const defaultOpenColumns = useMemo(() => {
      if (defaultOpenFirstColumn && columnContext) {
        const firstRootNode = Object.values(columnContext)[0];
        if (firstRootNode) {
          // Ensure the ID is converted to string as columnId.split() expects a string
          return [String(firstRootNode.id)];
        }
      }
      return undefined;
    }, [defaultOpenFirstColumn, columnContext]);

    // Use the custom hook for group column management
    const {
      columnStates,
      toggleColumn,
      isColumnHorizontal,
      columnVisibility: hookColumnVisibility,
    } = useGroupColumnManager(columnContextArray, defaultOpenColumns, hiddenColumns);

    // Helper functions for leaf node orientation management
    const toggleLeafNodeOrientation = useCallback((leafNodeId: string) => {
      setLeafNodeOrientations((prev) => ({
        ...prev,
        [leafNodeId]: !prev[leafNodeId], // Toggle orientation
      }));
    }, []);

    const isLeafNodeHorizontal = useCallback(
      (leafNodeId: string) => leafNodeOrientations[leafNodeId] ?? false, // Default to vertical
      [leafNodeOrientations],
    );

    // Initialize leaf node orientations when columnContext changes
    useMemo(() => {
      if (columnContext) {
        const newOrientations: Record<string, boolean> = {};

        const initializeLeafOrientations = (
          nodes: HierarchicalNode[],
          path: string[] = [],
        ) => {
          nodes.forEach((node) => {
            const currentPath = [...path, node.id];
            const currentPathKey = currentPath.join('.');

            if (node.subNodes.length === 0) {
              // This is a leaf node - initialize its orientation
              newOrientations[currentPathKey] = false; // Default to vertical
            } else {
              // Continue traversing children
              initializeLeafOrientations(node.subNodes, currentPath);
            }
          });
        };

        Object.values(columnContext).forEach((node) => {
          initializeLeafOrientations([node]);
        });

        setLeafNodeOrientations(newOrientations);
      }
    }, [columnContext]);

    // Group column navigation is managed by the useGroupColumnManager hook

    // Create column definitions
    const columnDefs = useMemo<ColumnDef<ScoreData>[]>(() => {
      if (!columns) {
        return [];
      }
      const baseColumns = columns.filter(
        (col) => !(col as AutoHierarchicalCol)?.ctxKey,
      ) as ColumnDef<ScoreData>[];

      // Flatten hierarchical columns into a single level
      const flattenedColumns: ColumnDef<ScoreData>[] = [];

      const addColumnsRecursively = (
        nodes: HierarchicalNode[],
        columnDef: AutoHierarchicalCol,
        path: string[] = [],
      ) => {
        nodes.forEach((node) => {
          const currentPath = [...path, node.id];
          const currentPathKey = currentPath.join('.');
          const columnState = columnStates[currentPathKey];

          if (node.subNodes.length === 0) {
            // Leaf node - add as a column with its own orientation management
            const isHorizontal = isLeafNodeHorizontal(currentPathKey);

            flattenedColumns.push({
              id: currentPathKey,
              accessorKey: currentPathKey,
              header: createLeafNodeHeaderRenderer(node.nodeName, isHorizontal, () =>
                toggleLeafNodeOrientation(currentPathKey),
              ),
              size: 0,
              // eslint-disable-next-line react/no-unstable-nested-components
              cell: ({ row, column }) => {
                const value = columnDef.closedValueGetter({
                  data: row.original,
                  colId: currentPathKey,
                });

                const columnIndex = column.columnDef.meta?.columnIndex ?? -1;

                // Use custom cell component if provided, otherwise use default
                if (columnDef.cellComponent) {
                  // eslint-disable-next-line react/no-unstable-nested-components
                  const CustomCellComponent = columnDef.cellComponent;
                  return (
                    <CustomCellComponent
                      value={value}
                      isHorizontal={isHorizontal}
                      pathKey={currentPathKey}
                      data={row.original}
                      colId={currentPathKey}
                      report={columnDef.report}
                      columnIndex={columnIndex}
                    />
                  );
                }

                return createLeafNodeCellRenderer(value, isHorizontal, currentPathKey);
              },
              meta: {
                isLeaf: true,
                isHorizontal,
                nodeData: node,
                path: currentPath,
                pathKey: currentPathKey,
              },
            });
          } else {
            // Group node - add as a group header column
            flattenedColumns.push({
              id: currentPathKey,
              accessorKey: currentPathKey,
              header: createGroupHeaderRenderer(
                node.nodeName,
                columnState?.isOpen || false,
                isColumnHorizontal(currentPathKey),
                () => toggleColumn(currentPathKey),
              ),
              size: 0,
              // eslint-disable-next-line react/no-unstable-nested-components
              cell: ({ row, column }) => {
                const value = columnDef.closedValueGetter({
                  data: row.original,
                  colId: currentPathKey,
                });

                const isHorizontal = isColumnHorizontal(currentPathKey);
                const columnIndex = column.columnDef.meta?.columnIndex ?? -1;

                // Use custom cell component if provided, otherwise use default
                if (columnDef.cellComponent) {
                  // eslint-disable-next-line react/no-unstable-nested-components
                  const CustomCellComponent = columnDef.cellComponent;
                  return (
                    <CustomCellComponent
                      value={value}
                      isHorizontal={isHorizontal}
                      pathKey={currentPathKey}
                      data={row.original}
                      colId={currentPathKey}
                      report={columnDef.report}
                      columnIndex={columnIndex}
                    />
                  );
                }

                return createGroupCellRenderer(value, isHorizontal, currentPathKey);
              },
              meta: {
                isGroup: true,
                isOpen: columnState?.isOpen,
                nodeData: node,
                path: currentPath,
                pathKey: currentPathKey,
              },
            });

            // If open, add child columns
            if (columnState?.isOpen) {
              addColumnsRecursively(node.subNodes, columnDef, currentPath);
            }
          }
        });
      };

      // Process each hierarchical column
      const hierarchicalColumns = columns.filter((col): col is AutoHierarchicalCol =>
        Boolean((col as AutoHierarchicalCol)?.ctxKey),
      );

      hierarchicalColumns.forEach((col) => {
        const rootNode = columnContext?.[col.ctxKey];
        if (rootNode) {
          addColumnsRecursively([rootNode], col, []);
        }
      });

      const allColumns = [...baseColumns, ...flattenedColumns];

      // Add column index to each column's meta for font color determination
      return allColumns.map((col, index) => ({
        ...col,
        meta: {
          ...col.meta,
          columnIndex: index,
        },
      }));
    }, [
      columns,
      columnContext,
      columnStates,
      isColumnHorizontal,
      toggleColumn,
      isLeafNodeHorizontal,
      toggleLeafNodeOrientation,
    ]);

    // Column visibility is now managed by the hook

    // Create table instance
    const table = useReactTable({
      data,
      columns: columnDefs,
      state: {
        sorting,
        columnFilters,
        columnVisibility: hookColumnVisibility,
        columnOrder,
      },
      onSortingChange: setSorting,
      onColumnFiltersChange: setColumnFilters,
      onColumnOrderChange: setColumnOrder,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      defaultColumn: {
        size: 0, //starting column size - let content determine width
        minSize: 0, //enforced during column resizing
        maxSize: 500, //enforced during column resizing
      },
    });

    // Force table re-render when column states change
    const tableKey = JSON.stringify(columnStates);

    return (
      <Box
        sx={{
          height,
          width,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {/* Table */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <TableContainer key={tableKey}>
            <Table
              sx={{ minWidth: 'auto', width: 'auto' }}
              aria-label="hierarchical table"
            >
              <TableHead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header, index) => (
                      <TableCell
                        key={header.id}
                        sx={{
                          backgroundColor:
                            index === 1 ? 'rgba(107, 107, 107, 1)' : undefined,
                          position: 'relative',
                          width: 'fit-content',
                          whiteSpace: 'nowrap',
                          border: '1px solid #ddd',
                          px: 0.5,
                          py: 0.25,
                          verticalAlign: 'top',
                        }}
                        size="small"
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableHead>
              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell, index) => (
                      <TableCell
                        key={cell.id}
                        sx={{
                          backgroundColor:
                            index === 1 ? 'rgba(107, 107, 107, 1)' : undefined,
                          border: '1px solid #ddd',
                          textAlign: 'center',
                          width: 'fit-content',
                          whiteSpace: 'nowrap',
                          px: 0.5,
                          py: 0.25,
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={table.getFilteredRowModel().rows.length}
          page={table.getState().pagination.pageIndex}
          onPageChange={(_, newPage) => table.setPageIndex(newPage)}
          rowsPerPage={table.getState().pagination.pageSize}
          onRowsPerPageChange={(event) => {
            table.setPageSize(Number(event.target.value));
          }}
          rowsPerPageOptions={[10, 15, 25, 50, 100]}
        />
      </Box>
    );
  };

export default TSAdvancedHierarchicalScoreTable;
